import React, { useState, useEffect, useCallback } from 'react';
import { Box, Skeleton, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';
import { AdminMenuBar } from '../../utils/admin-menu-bar';
import { AdminCfR2 as AdminCfR2Api } from '../../../api/admin-cf-usage';

const CELL = { whiteSpace: 'nowrap', py: 0.5, px: 1.5 };
const HEAD_CELL = { ...CELL, fontWeight: 600 };

// R2 pricing: storage $0.015/GB-month, Class A $4.50/M ops, Class B $0.36/M ops.
// (Ignores the monthly included allowances, so this is an upper-bound estimate.)
const STORAGE_PRICE_PER_GB = 0.015;
const CLASS_A_PRICE = 4.5 / 1_000_000;
const CLASS_B_PRICE = 0.36 / 1_000_000;
export const r2MonthCost = (b) =>
    (b.bytes / 1024 ** 3) * STORAGE_PRICE_PER_GB + b.class_a * CLASS_A_PRICE + b.class_b * CLASS_B_PRICE;

function fmtBytes(n) {
    if (n == null) return '—';
    const u = ['B', 'KB', 'MB', 'GB', 'TB'];
    let v = n;
    let i = 0;
    while (v >= 1024 && i < u.length - 1) {
        v /= 1024;
        i++;
    }
    return `${v.toFixed(i === 0 ? 0 : 2)} ${u[i]}`;
}

function CfR2() {
    const [state, setState] = useState({ loading: false, data: [], error: null });

    const refresh = useCallback(() => AdminCfR2Api(setState), []);
    useEffect(() => {
        refresh();
    }, [refresh]);

    const buckets = state.data ?? [];

    return (
        <Stack direction='column' sx={{ width: '100%', height: '100%', minHeight: 0 }}>
            <AdminMenuBar apiItems={[{ label: 'Refresh', onClick: refresh }]} />
            <Box sx={{ overflow: 'auto', flex: 1 }}>
                {state.error && (
                    <Typography variant='caption' color='error' sx={{ display: 'block', mb: 1 }}>
                        {state.error}
                    </Typography>
                )}
                {buckets.length === 0 && !state.loading && !state.error ? (
                    <Typography variant='body2' color='text.secondary'>
                        No buckets found.
                    </Typography>
                ) : (
                    <Table size='small' sx={{ width: '100%' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell sx={HEAD_CELL}>Bucket</TableCell>
                                <TableCell sx={{ ...HEAD_CELL, textAlign: 'right' }}>Objects</TableCell>
                                <TableCell sx={{ ...HEAD_CELL, textAlign: 'right' }}>Storage</TableCell>
                                <TableCell sx={{ ...HEAD_CELL, textAlign: 'right' }}>Class A / B ops (month)</TableCell>
                                <TableCell sx={{ ...HEAD_CELL, textAlign: 'right' }}>Est. Cost (month)</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {state.loading
                                ? Array.from({ length: 3 }, (_, i) => (
                                      <TableRow key={i}>
                                          {[0, 1, 2, 3, 4].map((j) => (
                                              <TableCell key={j} sx={CELL}>
                                                  <Skeleton />
                                              </TableCell>
                                          ))}
                                      </TableRow>
                                  ))
                                : buckets.map((b) => (
                                      <TableRow key={b.bucket} hover>
                                          <TableCell sx={CELL}>{b.bucket}</TableCell>
                                          <TableCell sx={{ ...CELL, textAlign: 'right' }}>
                                              {b.objects.toLocaleString()}
                                          </TableCell>
                                          <TableCell sx={{ ...CELL, textAlign: 'right' }}>
                                              {fmtBytes(b.bytes)}
                                          </TableCell>
                                          <TableCell sx={{ ...CELL, textAlign: 'right' }}>
                                              {b.class_a.toLocaleString()} / {b.class_b.toLocaleString()}
                                          </TableCell>
                                          <TableCell sx={{ ...CELL, textAlign: 'right' }}>
                                              ${r2MonthCost(b).toFixed(4)}
                                          </TableCell>
                                      </TableRow>
                                  ))}
                        </TableBody>
                    </Table>
                )}
            </Box>
        </Stack>
    );
}

export { CfR2 };
